import { h } from '@omni-torn/ui';
import { capacity, capacityPerks, DEFAULT_AGENCY, FLIGHT_TYPES, flightText, tripEstimate, type FlightType } from '@omni-torn/shared/abroad';
import { fightLine, type FightSide } from '@omni-torn/shared/combat';
import { planUpgrades, readFactionTree, readFactionUpgrades, type FactionUpgradesOwned, type UpgradeBranch } from '@omni-torn/shared/faction-upgrades';
import { ENERGY_STACK_CAP, gymBonus, gymPlan, HAPPY_MAX, happyJump, readGyms, STATS, XANAX_COOLDOWN_HOURS, XANAX_ENERGY, type Gym, type Stat } from '@omni-torn/shared/gym';
import type { TornRefAnswer } from '@omni-torn/shared/torn-ref';
import { server, session, torn } from '../session';
import { box, money, num, problem, tabs, withTableCsv } from '../ui';
import { eloBody, MERITS_INTRO, meritsBody, respectBody } from './calc';
import { busy, checkbox, field, guess, howSure, load, numberInput, readNum, save, select, sure, tile } from './calc-kit';

/**
 * Calculators (website roadmap S28: TornStats' merit, respect and ProxiQuality simulators, Torn Intel's upgrade
 * simulator, weav3r's travel estimator): one page of tabs — the merit planner, respect and Elo (pages/calc.ts, also their
 * own pages under Torn), gym gains, a happy-jump planner, travel profit, a fight calculator and the faction upgrade
 * simulator. All worked out in this browser (packages/shared: gym.ts, combat.ts, abroad.ts, faction-upgrades.ts,
 * torn-sims.ts). Torn is asked only with your own key, on a click ("Use mine", "Load your faction's") — except Torn's
 * public gym list and upgrade tree, kept a day in this browser. Nothing goes to the Omni-Torn server; what you type stays
 * in this browser. Every tab says which of its numbers are Torn's own and which are estimates.
 */

const DAY = 86_400_000;
const STAT_LABEL: Record<Stat, string> = { strength: 'Strength', speed: 'Speed', defense: 'Defense', dexterity: 'Dexterity' };
const STAT_OPTIONS = STATS.map((s) => ({ value: s, label: STAT_LABEL[s] }));

// ---------------------------------------------------------------- your own numbers (one Torn call, on a click)

interface Mine {
  stats: Record<Stat, number> | null;
  happy: number | null;
  energy: number | null;
  life: number | null;
  /** Gym-gain perks per stat, as fractions. */
  gymPerks: Record<Stat, number>;
  /** Extra travel items from perks. */
  carry: number;
  gym: number | null;
}

const n = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);

/** Your battle stats (a Limited key), bars, perks and gym: one call, reused for 5 minutes. */
async function readMine(): Promise<Mine> {
  const level = session.get()?.access?.level ?? 4;
  const r = (await torn('/user/', { selections: level >= 3 ? 'battlestats,bars,perks,gym' : 'bars,perks,gym' }, 5 * 60_000)) as Record<string, unknown>;
  const bar = (k: string, f: 'current' | 'maximum' = 'current') => n((r[k] as Record<string, unknown> | undefined)?.[f]);
  const perks = Object.fromEntries(Object.entries(r).filter(([k]) => k.endsWith('_perks')));
  const stats = STATS.every((s) => n(r[s]) !== null) ? (Object.fromEntries(STATS.map((s) => [s, r[s] as number])) as Record<Stat, number>) : null;
  return { stats, happy: bar('happy'), energy: bar('energy'), life: bar('life', 'maximum'), gymPerks: gymBonus(perks), carry: capacityPerks(perks), gym: n(r.active_gym) };
}

const GYMS_KEY = 'omni-torn.tornGyms';

/** Torn's gyms (public): kept a day in this browser. */
async function tornGyms(): Promise<Gym[]> {
  const kept = load<{ at: number; gyms: Gym[] }>(GYMS_KEY, { at: 0, gyms: [] });
  if (kept.gyms.length && Date.now() - kept.at < DAY) return kept.gyms;
  const gyms = readGyms(await torn('/torn/', { selections: 'gyms' }, DAY));
  if (!gyms.length) throw new Error('Torn didn’t send its gyms.');
  save(GYMS_KEY, { at: Date.now(), gyms });
  return gyms;
}
const keptGyms = (): Gym[] => load<{ at: number; gyms: Gym[] }>(GYMS_KEY, { at: 0, gyms: [] }).gyms;

// ---------------------------------------------------------------- gym gains and happy jumps (one state)

const GYM_KEY = 'omni-torn.gymCalc';

interface GymState {
  stat: Stat;
  values: Record<Stat, number>;
  happy: number;
  /** A gym from Torn's list (null: dots typed in). */
  gymId: number | null;
  dots: number;
  energyPerTrain: number;
  energy: number;
  /** Gym-gain perks per stat, percent. */
  bonus: Record<Stat, number>;
  // the happy jump
  boost: number;
  ecstasy: boolean;
  xanax: number;
  energyStart: number;
  energyExtra: number;
}

const GYM_DEFAULT: GymState = {
  stat: 'strength',
  values: { strength: 10_000_000, speed: 10_000_000, defense: 10_000_000, dexterity: 10_000_000 },
  happy: 5025,
  gymId: null,
  dots: 7.3,
  energyPerTrain: 10,
  energy: 150,
  bonus: { strength: 0, speed: 0, defense: 0, dexterity: 0 },
  boost: 0,
  ecstasy: true,
  xanax: 4,
  energyStart: 150,
  energyExtra: 0,
};

/** The gym part both tabs share: stat, its value, happiness, the gym (Torn's list or typed dots), perks — and "Use mine". */
function gymInputs(st: GymState, changed: () => void, opts: { energy: boolean; onMine?: () => void }) {
  const status = h('span', { class: 'muted small', role: 'status', 'data-calc-status': '' });
  const stat = select('Stat to train', STAT_OPTIONS, st.stat, 'g-stat');
  const value = numberInput('The stat now', st.values[st.stat], { min: 0, id: 'g-value' });
  const happy = numberInput('Happiness', st.happy, { min: 0, max: HAPPY_MAX, id: 'g-happy' });
  const gym = h('select', { 'aria-label': 'Gym', id: 'g-gym' }) as HTMLSelectElement;
  const dots = numberInput('Gym dots for this stat', st.dots, { min: 0, max: 20, step: 'any', id: 'g-dots' });
  const per = numberInput('Energy per train', st.energyPerTrain, { min: 1, max: 100, id: 'g-per' });
  const energy = numberInput('Energy to spend', st.energy, { min: 0, max: 100_000, id: 'g-energy' });
  const bonus = numberInput('Gym-gain perks (%)', st.bonus[st.stat], { min: 0, max: 500, step: 'any', id: 'g-bonus' });
  const fillGyms = () => {
    const list = keptGyms();
    gym.replaceChildren(h('option', { value: '' }, list.length ? 'Typed in (dots below)' : 'Typed in — “Torn’s gyms” lists them'), ...list.map((g) => h('option', { value: String(g.id), ...(g.id === st.gymId ? { selected: true } : {}) }, `${g.name} (${g.energy}E)`)));
  };
  /** The chosen gym's dots and energy for the stat (Torn's numbers win over typed ones). */
  const fromGym = () => {
    const g = keptGyms().find((x) => x.id === st.gymId);
    if (!g) return;
    st.dots = g.dots[st.stat];
    st.energyPerTrain = g.energy;
    dots.value = String(st.dots);
    per.value = String(st.energyPerTrain);
  };
  const read = () => {
    st.values[st.stat] = readNum(value, 0, 1e13);
    st.happy = readNum(happy, 0, HAPPY_MAX);
    st.dots = readNum(dots, 0, 20);
    st.energyPerTrain = Math.round(readNum(per, 1, 100, 10));
    st.energy = Math.round(readNum(energy, 0, 100_000));
    st.bonus[st.stat] = readNum(bonus, 0, 500);
    save(GYM_KEY, st);
    changed();
  };
  stat.addEventListener('change', () => {
    st.stat = stat.value as Stat;
    value.value = String(st.values[st.stat]);
    bonus.value = String(st.bonus[st.stat]);
    fromGym();
    read();
  });
  gym.addEventListener('change', () => {
    st.gymId = gym.value ? Number(gym.value) : null;
    fromGym();
    read();
  });
  dots.addEventListener('input', () => {
    st.gymId = null;
    gym.value = '';
    read();
  });
  per.addEventListener('input', () => {
    st.gymId = null;
    gym.value = '';
    read();
  });
  for (const el of [value, happy, energy, bonus]) el.addEventListener('input', read);
  fillGyms();
  const mine = h('button', { type: 'button', class: 'btn-small', 'data-calc-mine': '' }, 'Use mine');
  const gymsBtn = h('button', { type: 'button', class: 'btn-small', 'data-calc-gyms': '' }, 'Torn’s gyms');
  mine.addEventListener('click', () =>
    void busy(mine, status, 'Asking Torn…', async () => {
      const m = await readMine();
      if (m.stats) st.values = { ...m.stats };
      if (m.happy !== null) st.happy = m.happy;
      if (m.energy !== null) {
        st.energy = m.energy;
        st.energyStart = m.energy;
      }
      st.bonus = Object.fromEntries(STATS.map((s) => [s, Math.round(m.gymPerks[s] * 1000) / 10])) as Record<Stat, number>;
      if (m.gym !== null) {
        const gyms = await tornGyms().catch(() => keptGyms());
        if (gyms.some((g) => g.id === m.gym)) st.gymId = m.gym;
      }
      fillGyms();
      fromGym();
      value.value = String(st.values[st.stat]);
      happy.value = String(st.happy);
      energy.value = String(st.energy);
      bonus.value = String(st.bonus[st.stat]);
      opts.onMine?.();
      read();
      status.textContent = `From your key: ${m.stats ? 'battle stats, ' : ''}happiness, energy, perks${st.gymId ? ' and your gym' : ''}.${m.stats ? '' : ' (Battle stats need a Limited key — type your stat in.)'}`;
    }),
  );
  gymsBtn.addEventListener('click', () =>
    void busy(gymsBtn, status, 'Asking Torn…', async () => {
      const gyms = await tornGyms();
      fillGyms();
      status.textContent = `${gyms.length} gyms from Torn — pick yours.`;
    }),
  );
  const fields = [field('Stat', stat, 'g-stat'), field('The stat now', value, 'g-value'), field('Happiness', happy, 'g-happy'), field('Gym', gym, 'g-gym'), field('Gym dots', dots, 'g-dots'), field('Energy per train', per, 'g-per'), ...(opts.energy ? [field('Energy to spend', energy, 'g-energy')] : []), field('Gym-gain perks %', bonus, 'g-bonus')];
  return { fields, toolbar: h('div', { class: 'toolbar' }, mine, gymsBtn, status) };
}

const gymSources = () =>
  howSure(
    sure('a gym’s dots and energy per train (Torn’s gym list, read with your key), your happiness, energy and perks.'),
    guess('the gain itself — the community’s fitted gym formula (Vladar’s, quoted on Torn’s wiki; the extension’s gym helper uses the same). Torn adds randomness; real gains are usually within a few percent.'),
    guess('happiness falls by about half the energy each train (players’ observation).'),
    guess('stats over 50 million count for a little less (the formula’s soft cap).'),
  );

function gymBody(): HTMLElement[] {
  const st = load<GymState>(GYM_KEY, GYM_DEFAULT);
  const out = h('div', { 'data-gym-out': '' });
  const each = h('div', {});
  const draw = () => {
    const p = gymPlan({ stat: st.stat, value: st.values[st.stat], happy: st.happy, dots: st.dots, energyPerTrain: st.energyPerTrain, energy: st.energy, bonusPct: st.bonus[st.stat] });
    out.replaceChildren(
      h(
        'div',
        { class: 'tiles' },
        tile('Trains', num(p.trains)),
        tile('First train', `+${num(p.perTrain, 1)}`),
        h('div', { class: 'tile tile-total' }, h('div', { class: 'tile-label' }, `${STAT_LABEL[st.stat]} gained in all`), h('div', { class: 'tile-value', 'data-value': String(Math.round(p.total)) }, `+${num(p.total)}`)),
        tile('Per 100 energy', p.per100 === null ? '—' : `+${num(p.per100, 1)}`),
      ),
      p.left ? h('p', { class: 'muted small' }, `${num(p.left)} energy left over (less than a train).`) : '',
    );
    const g = keptGyms().find((x) => x.id === st.gymId);
    each.replaceChildren(
      g
        ? box(`Every stat at ${g.name}`, withTableCsv(h('div', { class: 'table-wrap' }, h('table', {}, h('thead', {}, h('tr', {}, h('th', {}, 'Stat'), h('th', { class: 'r' }, 'Dots'), h('th', { class: 'r' }, 'Now'), h('th', { class: 'r' }, `Gain for ${num(st.energy)} energy`))), h('tbody', {}, ...STATS.map((s) => {
          const q = gymPlan({ stat: s, value: st.values[s], happy: st.happy, dots: g.dots[s], energyPerTrain: g.energy, energy: st.energy, bonusPct: st.bonus[s] });
          return h('tr', { 'data-stat': s }, h('td', {}, STAT_LABEL[s]), h('td', { class: 'r' }, g.dots[s] ? num(g.dots[s], 1) : 'can’t train'), h('td', { class: 'r' }, num(st.values[s])), h('td', { class: 'r' }, g.dots[s] ? `+${num(q.total)}` : '—'));
        }))))), { id: 'gym-each' })
        : '',
    );
  };
  const inputs = gymInputs(st, draw, { energy: true });
  draw();
  return [box('Gym gains', [h('div', { class: 'calc-grid' }, ...inputs.fields), inputs.toolbar, out], { id: 'gym-calc' }), each, gymSources()];
}

function jumpBody(): HTMLElement[] {
  const st = load<GymState>(GYM_KEY, GYM_DEFAULT);
  const out = h('div', { 'data-jump-out': '' });
  const boost = numberInput('Happiness added by boosters', st.boost, { min: 0, max: HAPPY_MAX, id: 'j-boost' });
  const xanax = numberInput('Xanax', st.xanax, { min: 0, max: 20, id: 'j-xanax' });
  const start = numberInput('Energy when you start', st.energyStart, { min: 0, max: 1000, id: 'j-start' });
  const extra = numberInput('Other energy (cans, refill…)', st.energyExtra, { min: 0, max: 10_000, id: 'j-extra' });
  const draw = () => {
    const j = happyJump({ stat: st.stat, value: st.values[st.stat], happy: st.happy, dots: st.dots, energyPerTrain: st.energyPerTrain, bonusPct: st.bonus[st.stat], boost: st.boost, ecstasy: st.ecstasy, xanax: st.xanax, energyStart: st.energyStart, energyExtra: st.energyExtra });
    out.replaceChildren(
      h(
        'div',
        { class: 'tiles' },
        tile('Energy to train', num(j.energy), j.overCap ? 'tile-bad' : ''),
        tile('Happiness at the start', num(j.happy)),
        h('div', { class: 'tile tile-total' }, h('div', { class: 'tile-label' }, 'The jump gains'), h('div', { class: 'tile-value', 'data-value': String(Math.round(j.jump.total)) }, `+${num(j.jump.total)}`)),
        tile('At your usual happiness', `+${num(j.normal.total)}`),
        tile('The jump adds', `+${num(j.extra)}${j.normal.total > 0 ? ` (×${num(j.jump.total / j.normal.total, 2)})` : ''}`),
      ),
      h('p', { class: 'muted small' }, `${num(j.jump.trains)} trains of ${num(st.energyPerTrain)} energy at ${num(st.dots, 1)} dots. ${st.xanax > 1 ? `Stacking ${num(st.xanax)} Xanax takes about ${num(j.stackHours[0])}–${num(j.stackHours[1])} hours of cooldowns (don’t train meanwhile).` : ''}`),
      j.overCap ? h('p', { class: 't-red small', 'data-jump-cap': '' }, `Over ${num(ENERGY_STACK_CAP)} energy: players report Torn won’t let you use more energy items past about that — plan for ${num(ENERGY_STACK_CAP)} or less.`) : '',
      h('p', { class: 'muted small' }, 'Xanax and Ecstasy can overdose you (which costs energy and happiness and stops the jump) — this calculator doesn’t count that risk.'),
    );
  };
  const read = () => {
    st.boost = readNum(boost, 0, HAPPY_MAX);
    st.xanax = Math.round(readNum(xanax, 0, 20));
    st.energyStart = Math.round(readNum(start, 0, 1000));
    st.energyExtra = Math.round(readNum(extra, 0, 10_000));
    save(GYM_KEY, st);
    draw();
  };
  for (const el of [boost, xanax, start, extra]) el.addEventListener('input', read);
  const inputs = gymInputs(st, draw, { energy: false, onMine: () => (start.value = String(st.energyStart)) });
  draw();
  return [
    box('Happy jump', [
      h('p', { class: 'muted small' }, 'Stack energy (Xanax), raise your happiness (boosters, then Ecstasy doubles it), then train it all at once — against training the same energy at your usual happiness.'),
      h('div', { class: 'calc-grid' }, ...inputs.fields, field('Happiness from boosters', boost, 'j-boost'), field('Xanax (+250 energy each)', xanax, 'j-xanax'), field('Energy when you start', start, 'j-start'), field('Other energy', extra, 'j-extra')),
      h('div', { class: 'toolbar' }, checkbox('Ecstasy last (doubles happiness)', st.ecstasy, 'j-ecstasy', (on) => ((st.ecstasy = on), save(GYM_KEY, st), draw()))),
      inputs.toolbar,
      out,
    ], { id: 'jump-calc' }),
    howSure(
      sure(`Xanax gives ${XANAX_ENERGY} energy and Ecstasy doubles happiness (Torn’s item descriptions).`),
      guess(`a Xanax’s drug cooldown is about ${XANAX_COOLDOWN_HOURS[0]}–${XANAX_COOLDOWN_HOURS[1]} hours (Torn picks it at random; players’ reports).`),
      guess(`the ${num(ENERGY_STACK_CAP)}-energy limit for energy items (players’ reports, not confirmed by Torn).`),
      guess(`happiness tops out at ${num(HAPPY_MAX)} here (the gym formula’s own top), and the gains use the same estimated gym formula as Gym gains.`),
      guess('happiness from boosters: type what the items you use say; they aren’t built in.'),
    ),
  ];
}

// ---------------------------------------------------------------- travel profit

const TRIP_KEY = 'omni-torn.tripCalc';
const FLIGHT_LABEL: Record<FlightType, string> = { standard: 'Standard', airstrip: 'Airstrip', private: 'WLT business', business: 'Business class' };

interface TripState {
  country: string;
  type: FlightType;
  extra: number;
  buy: number;
  sell: number;
  fee: number;
  /** Blank: as many as you can carry. */
  stock: number | null;
}

interface SeenItem {
  id: number;
  name: string | null;
  quantity: number;
  cost: number;
}

function tripBody(): HTMLElement[] {
  const st = load<TripState>(TRIP_KEY, { country: 'mex', type: 'standard', extra: 0, buy: 500, sell: 2000, fee: 0, stock: null });
  const countries = DEFAULT_AGENCY.countries;
  const country = select('Country', countries.map((c) => ({ value: c.code, label: c.name })), st.country, 't-country');
  const type = select('Flight', FLIGHT_TYPES.map((t) => ({ value: t, label: FLIGHT_LABEL[t] })), st.type, 't-type');
  const extra = numberInput('Extra items (perks)', st.extra, { min: 0, max: 100, id: 't-extra' });
  const buy = numberInput('Price abroad', st.buy, { min: 0, id: 't-buy' });
  const sell = numberInput('Sells for at home', st.sell, { min: 0, id: 't-sell' });
  const fee = numberInput('Selling fee %', st.fee, { min: 0, max: 100, step: 'any', id: 't-fee' });
  const stock = numberInput('In stock there (blank: plenty)', st.stock ?? 0, { min: 0, id: 't-stock' });
  if (st.stock === null) stock.value = '';
  const status = h('span', { class: 'muted small', role: 'status', 'data-calc-status': '' });
  const seen = h('select', { 'aria-label': 'Items seen there', id: 't-seen', hidden: true }) as HTMLSelectElement;
  let shelves: Record<string, SeenItem[]> | null = null;
  let values = new Map<number, number | null>();
  const out = h('div', { 'data-trip-out': '' });
  const fillSeen = () => {
    const list = shelves?.[st.country] ?? null;
    seen.hidden = !list;
    if (!list) return;
    seen.replaceChildren(h('option', { value: '' }, list.length ? `${list.length} items seen in ${countries.find((c) => c.code === st.country)?.name ?? ''} — pick one` : 'Nothing seen there lately'), ...list.map((i) => h('option', { value: String(i.id) }, `${i.name ?? `Item ${i.id}`} · ${money(i.cost)} · ${num(i.quantity)} left`)));
  };
  const draw = () => {
    const c = countries.find((x) => x.code === st.country) ?? countries[0]!;
    const cap = capacity(st.type, st.extra);
    const e = tripEstimate(c, st.type, cap, st.buy, st.sell, st.fee, st.stock);
    if (!e) {
      out.replaceChildren(h('p', { class: 'muted' }, 'No flight time for that.'));
      return;
    }
    out.replaceChildren(
      h(
        'div',
        { class: 'tiles' },
        tile('You bring', `${num(e.amount)} of ${num(cap)}`),
        tile('Profit each', money(e.each), e.each < 0 ? 'tile-bad' : ''),
        h('div', { class: `tile tile-total${e.trip < 0 ? ' tile-bad' : ''}` }, h('div', { class: 'tile-label' }, 'The trip'), h('div', { class: 'tile-value', 'data-value': String(e.trip) }, money(e.trip))),
        tile('Per hour flying', money(e.perHour)),
        tile(`A day (${num(e.perDay.trips)} trips)`, money(e.perDay.profit)),
      ),
      h('p', { class: 'muted small' }, `${flightText(c.seconds[st.type] ?? 0)} each way (${flightText(e.roundTrip)} there and back)${st.type === 'standard' && c.ticket ? `; the ${money(c.ticket)} ticket is taken off` : ''}. A day of trips counts 2 minutes in the shop each time and assumes the stock and prices hold.`),
    );
  };
  const read = () => {
    st.country = country.value;
    st.type = type.value as FlightType;
    st.extra = Math.round(readNum(extra, 0, 100));
    st.buy = readNum(buy, 0, 1e12);
    st.sell = readNum(sell, 0, 1e12);
    st.fee = readNum(fee, 0, 100);
    st.stock = stock.value.trim() === '' ? null : Math.round(readNum(stock, 0, 1e7));
    save(TRIP_KEY, st);
    draw();
  };
  country.addEventListener('change', () => {
    read();
    fillSeen();
  });
  type.addEventListener('change', read);
  for (const el of [extra, buy, sell, fee, stock]) el.addEventListener('input', read);
  seen.addEventListener('change', () => {
    const it = shelves?.[st.country]?.find((i) => i.id === Number(seen.value));
    if (!it) return;
    buy.value = String(it.cost);
    stock.value = String(it.quantity);
    const v = values.get(it.id);
    if (v) sell.value = String(v);
    read();
  });
  const mine = h('button', { type: 'button', class: 'btn-small', 'data-calc-mine': '' }, 'Use my perks');
  mine.addEventListener('click', () =>
    void busy(mine, status, 'Asking Torn…', async () => {
      const m = await readMine();
      extra.value = String(m.carry);
      read();
      status.textContent = m.carry ? `Your perks add ${num(m.carry)} items.` : 'No travel-capacity perks found on your key.';
    }),
  );
  const look = h('button', { type: 'button', class: 'btn-small', 'data-trip-seen': '' }, 'Items seen there');
  look.addEventListener('click', () =>
    void busy(look, status, 'Asking the Omni-Torn server…', async () => {
      const [f, items] = await Promise.all([server<{ countries: Record<string, { items: SeenItem[] }> }>('GET', '/api/travel/forecast'), server<TornRefAnswer<'items'>>('GET', '/api/torn/items').catch(() => null)]);
      shelves = Object.fromEntries(Object.entries(f.countries ?? {}).map(([k, v]) => [k, v.items ?? []]));
      values = new Map((items?.data ?? []).map((i) => [i.id, i.marketValue ?? null]));
      fillSeen();
      status.textContent = 'Shops abroad as Omni-Torn users saw them (pooled, anonymous); “sells for” is Torn’s market value.';
    }),
  );
  draw();
  return [
    box('Travel profit', [h('div', { class: 'calc-grid' }, field('Country', country, 't-country'), field('Flight', type, 't-type'), field('Extra items (perks)', extra, 't-extra'), field('Price abroad', buy, 't-buy'), field('Sells for at home', sell, 't-sell'), field('Selling fee %', fee, 't-fee'), field('In stock there', stock, 't-stock')), h('div', { class: 'toolbar' }, mine, look, seen, status), out], { id: 'trip-calc' }),
    h('p', { class: 'muted small' }, 'Every country and item side by side, with restock forecasts: ', h('a', { href: '/travel', 'data-link': true }, 'Travel'), '.'),
    howSure(
      sure('flight times and standard tickets as Torn’s travel agency listed them in October 2026; your perks (read with your key); prices you type or Torn’s market value.'),
      guess('what you can carry — Omni-Torn’s travel-table rule (10 items, +5 on any flight but standard, + your perks); check it against what Torn lets you buy.'),
      guess('a day of trips assumes the shop has the stock each time and the price holds.'),
    ),
  ];
}

// ---------------------------------------------------------------- fight (TornStats' ProxiQuality-style)

const FIGHT_KEY = 'omni-torn.fightCalc';
interface FightState {
  you: Required<FightSide>;
  them: Required<FightSide>;
}
const SIDE_DEFAULT: Required<FightSide> = { strength: 10_000_000, defense: 10_000_000, speed: 10_000_000, dexterity: 10_000_000, weaponDamage: 55, damageBonus: 0, hitBonus: 0, armor: 30, life: 7500 };
const FIGHT_ROWS: ReadonlyArray<{ k: keyof FightSide; label: string; max: number; step?: 'any' }> = [
  { k: 'strength', label: 'Strength', max: 1e13 },
  { k: 'defense', label: 'Defense', max: 1e13 },
  { k: 'speed', label: 'Speed', max: 1e13 },
  { k: 'dexterity', label: 'Dexterity', max: 1e13 },
  { k: 'weaponDamage', label: 'Weapon damage', max: 1000, step: 'any' },
  { k: 'damageBonus', label: 'Damage bonus %', max: 1000, step: 'any' },
  { k: 'hitBonus', label: 'Hit chance bonus (points)', max: 100, step: 'any' },
  { k: 'armor', label: 'Armor % (where they hit you)', max: 95, step: 'any' },
  { k: 'life', label: 'Life', max: 1e7 },
];

function fightBody(): HTMLElement[] {
  const st = load<FightState>(FIGHT_KEY, { you: { ...SIDE_DEFAULT }, them: { ...SIDE_DEFAULT } });
  st.you = { ...SIDE_DEFAULT, ...st.you };
  st.them = { ...SIDE_DEFAULT, ...st.them };
  const out = h('div', { 'data-fight-out': '' });
  const status = h('span', { class: 'muted small', role: 'status', 'data-calc-status': '' });
  const inputs: Record<'you' | 'them', Partial<Record<keyof FightSide, HTMLInputElement>>> = { you: {}, them: {} };
  const pct = (x: number) => `${num(x * 100, 1)}%`;
  const draw = () => {
    const mineHit = fightLine(st.you, st.them);
    const theirHit = fightLine(st.them, st.you);
    const row = (label: string, a: string, b: string, key?: string) => h('tr', key ? { 'data-fight': key } : {}, h('th', { scope: 'row' }, label), h('td', { class: 'r' }, a), h('td', { class: 'r' }, b));
    out.replaceChildren(
      withTableCsv(h(
        'div',
        { class: 'table-wrap' },
        h(
          'table',
          { class: 'calc-vs' },
          h('thead', {}, h('tr', {}, h('th', {}, ''), h('th', { class: 'r' }, 'You hit them'), h('th', { class: 'r' }, 'They hit you'))),
          h(
            'tbody',
            {},
            row('Hit chance', pct(mineHit.hitChance), pct(theirHit.hitChance), 'hit'),
            row('…from speed ÷ dexterity alone', pct(mineHit.baseHitChance), pct(theirHit.baseHitChance)),
            row('Blocked by defense', pct(mineHit.mitigation), pct(theirHit.mitigation), 'blocked'),
            row('Head, throat, heart (×3.5)', num(mineHit.damage.head), num(theirHit.damage.head)),
            row('Chest, stomach, groin (×2)', num(mineHit.damage.chest), num(theirHit.damage.chest), 'chest'),
            row('Arms, legs (×1)', num(mineHit.damage.limbs), num(theirHit.damage.limbs)),
            row('Hands, feet (×0.7)', num(mineHit.damage.extremities), num(theirHit.damage.extremities)),
            row('Per turn, chest, misses counted', num(mineHit.perTurnChest), num(theirHit.perTurnChest)),
            row('Chest hits to finish', mineHit.hitsToFinish === null ? '—' : num(mineHit.hitsToFinish), theirHit.hitsToFinish === null ? '—' : num(theirHit.hitsToFinish), 'finish'),
          ),
        ),
      )),
    );
  };
  const read = () => {
    for (const side of ['you', 'them'] as const) {
      for (const r of FIGHT_ROWS) {
        const el = inputs[side][r.k];
        if (el) st[side][r.k] = readNum(el, 0, r.max);
      }
    }
    save(FIGHT_KEY, st);
    draw();
  };
  const grid = h(
    'table',
    { class: 'calc-vs calc-inputs' },
    h('thead', {}, h('tr', {}, h('th', {}, ''), h('th', {}, 'You'), h('th', {}, 'Them'))),
    h(
      'tbody',
      {},
      ...FIGHT_ROWS.map((r) =>
        h(
          'tr',
          {},
          h('th', { scope: 'row' }, r.label),
          ...(['you', 'them'] as const).map((side) => {
            const el = numberInput(`${side === 'you' ? 'Your' : 'Their'} ${r.label.toLowerCase()}`, st[side][r.k], { min: 0, max: r.max, ...(r.step ? { step: r.step } : {}), id: `f-${side}-${r.k}` });
            el.addEventListener('input', read);
            inputs[side][r.k] = el;
            return h('td', {}, el);
          }),
        ),
      ),
    ),
  );
  const mine = h('button', { type: 'button', class: 'btn-small', 'data-calc-mine': '' }, 'Use mine');
  mine.addEventListener('click', () =>
    void busy(mine, status, 'Asking Torn…', async () => {
      const m = await readMine();
      if (!m.stats) throw new Error('Torn sends your battle stats only to a Limited key — type them in.');
      for (const s of STATS) inputs.you[s]!.value = String(m.stats[s]);
      if (m.life) inputs.you.life!.value = String(m.life);
      read();
      status.textContent = 'Your battle stats and life from your key (stat bonuses from perks aren’t added — Torn’s battle stats are before them).';
    }),
  );
  const swap = h('button', { type: 'button', class: 'btn-small', 'data-fight-swap': '' }, 'Swap sides');
  swap.addEventListener('click', () => {
    for (const r of FIGHT_ROWS) {
      const a = inputs.you[r.k]!.value;
      inputs.you[r.k]!.value = inputs.them[r.k]!.value;
      inputs.them[r.k]!.value = a;
    }
    read();
  });
  draw();
  return [
    box('Fight calculator', [h('p', { class: 'muted small' }, 'Both sides’ battle stats and weapons: who hits how often, how much the defense blocks and the damage to each body part — like TornStats’ ProxiQuality calculator. Their stats: a spy or Omni-Torn’s estimate.'), withTableCsv(h('div', { class: 'table-wrap' }, grid)), h('div', { class: 'toolbar' }, mine, swap, status), out], { id: 'fight-calc' }),
    howSure(
      sure('body part multipliers (head, throat and heart ×3.5; chest, stomach and groin ×2; arms and legs ×1; hands and feet ×0.7 — Torn’s wiki).'),
      sure('the benchmark points Torn published for hit chance (speed ÷ dexterity) and the damage defense blocks (defense ÷ strength) — wiki, Attacking 2.0; Torn calls the hit-chance table an approximation itself.'),
      guess('between those points the two curves are fitted (they pass through every benchmark; the same curves Omni-Torn’s estimates use).'),
      guess('damage from strength (7·log²(STR/10) + 27·log(STR/10) + 30, a community formula) × weapon damage ÷ 10 — the same model Omni-Torn’s estimates use; real hits vary.'),
      guess('weapon accuracy, weapon quality, masteries, temporary items, passives and special effects aren’t modelled — add what you reckon they give as the hit chance and damage bonuses.'),
    ),
  ];
}

// ---------------------------------------------------------------- faction upgrades (Torn Intel's simulator)

const TREE_KEY = 'omni-torn.factionTree';
const UPGRADES_KEY = 'omni-torn.upgradePlan';

interface UpgradeState {
  want: Record<number, number>;
  /** Branch name → the multiplier you set for a branch your faction hasn't started. */
  mult: Record<string, number>;
}

/** Torn's upgrade tree (public): kept a day in this browser. */
async function upgradeTree(): Promise<UpgradeBranch[]> {
  const kept = load<{ at: number; tree: UpgradeBranch[] }>(TREE_KEY, { at: 0, tree: [] });
  if (kept.tree.length && Date.now() - kept.at < DAY) return kept.tree;
  const tree = readFactionTree(await torn('/v2/torn/factiontree', {}, DAY));
  if (!tree.length) throw new Error('Torn’s upgrade tree came back in a shape this page doesn’t know.');
  save(TREE_KEY, { at: Date.now(), tree });
  return tree;
}

/** Your faction's upgrades (in memory for this visit only) and its respect. */
let owned: FactionUpgradesOwned | null = null;
let factionRespect: number | null = null;

function upgradesBody(): HTMLElement[] {
  const st = load<UpgradeState>(UPGRADES_KEY, { want: {}, mult: {} });
  const status = h('span', { class: 'muted small', role: 'status', 'data-calc-status': '' });
  const tiles = h('div', { class: 'tiles', 'data-upgrades-plan': '' });
  const branches = h('div', { 'data-upgrade-branches': '' }, h('p', { class: 'muted' }, 'Reading Torn’s upgrade tree…'));
  const challenges = h('div', {});
  let tree: UpgradeBranch[] = [];
  const have = (id: number) => owned?.levels[id] ?? 0;
  /** Core upgrades: Torn gives them no branch multiplier (× 1). */
  const isCore = (b: UpgradeBranch) => /^core$/i.test(b.name) || b.lines.some((l) => owned?.core.includes(l.id));
  const tornMult = (branch: string) => owned?.branches.find((b) => b.name === branch)?.multiplier ?? null;
  const multOf = (branch: string) => (isCore(tree.find((b) => b.name === branch) ?? { name: branch, lines: [] }) ? 1 : (tornMult(branch) ?? st.mult[branch] ?? 1));
  const totals = () => {
    const plan = planUpgrades(tree, owned?.levels ?? {}, st.want, multOf);
    tiles.replaceChildren(
      h('div', { class: 'tile tile-total' }, h('div', { class: 'tile-label' }, 'This plan costs'), h('div', { class: 'tile-value', 'data-value': String(plan.cost) }, `${num(plan.cost)} respect`)),
      tile('Levels added', num(plan.levels)),
      tile('Your faction’s respect', factionRespect === null ? '—' : num(factionRespect)),
      tile('Its upgrades cost', owned ? num(owned.spent) : '—'),
    );
    challenges.replaceChildren(
      plan.challenges.length
        ? box(`Challenges first (${plan.challenges.length})`, h('ul', {}, ...plan.challenges.map((c) => h('li', {}, h('strong', {}, `${c.upgrade} ${c.level}: `), c.challenge))), { id: 'upgrade-challenges' })
        : '',
    );
    save(UPGRADES_KEY, st);
  };
  const drawTree = () => {
    branches.replaceChildren(
      ...tree.map((b) => {
        const core = isCore(b);
        const torns = core ? 1 : tornMult(b.name);
        const m = numberInput(`${b.name}: cost multiplier`, multOf(b.name), { min: 1, max: 50, step: 'any' });
        if (torns !== null) m.setAttribute('disabled', '');
        m.addEventListener('input', () => {
          st.mult[b.name] = readNum(m, 1, 50, 1);
          drawRows();
          totals();
        });
        const body = h('tbody', {});
        const drawRows = () =>
          body.replaceChildren(
            ...b.lines.map((line) => {
              const max = line.levels[line.levels.length - 1]!.level;
              const from = have(line.id);
              const want = h('select', { 'aria-label': `${line.name}: level wanted` }, ...Array.from({ length: max - from + 1 }, (_, i) => from + i).map((l) => h('option', { value: String(l), ...(l === Math.max(from, st.want[line.id] ?? from) ? { selected: true } : {}) }, String(l)))) as HTMLSelectElement;
              const cost = h('td', { class: 'r' });
              const then = h('td', { class: 'small' });
              const fill = () => {
                const to = Number(want.value);
                st.want[line.id] = to;
                const c = line.levels.filter((l) => l.level > from && l.level <= to).reduce((s, l) => s + l.cost * multOf(b.name), 0);
                cost.textContent = c ? num(c) : '—';
                then.textContent = line.levels.find((l) => l.level === Math.max(to, from))?.ability ?? (to ? '' : '—');
              };
              want.addEventListener('change', () => {
                fill();
                totals();
              });
              fill();
              return h('tr', { 'data-upgrade': String(line.id) }, h('td', {}, h('strong', {}, line.name), h('div', { class: 'muted small' }, `${num(line.levels.length)} level${line.levels.length === 1 ? '' : 's'} · ${line.levels.map((l) => num(l.cost)).join(' / ')}`)), h('td', { class: 'r' }, String(from)), h('td', {}, want), cost, then);
            }),
          );
        drawRows();
        return box(
          h('h2', {}, b.name),
          [
            h('div', { class: 'toolbar small' }, h('label', {}, 'Respect cost × ', m), h('span', { class: 'muted' }, core ? ' Core upgrades: Torn’s API gives them no branch multiplier.' : torns !== null ? ' Torn’s multiplier for your faction’s branch.' : owned ? ' Your faction hasn’t started this branch: Torn gives no multiplier — set what its Upgrades page shows.' : ' Load your faction’s upgrades for Torn’s multiplier, or set it.')),
            withTableCsv(h('div', { class: 'table-wrap' }, h('table', {}, h('thead', {}, h('tr', {}, h('th', {}, 'Upgrade'), h('th', { class: 'r' }, 'Have'), h('th', {}, 'Want'), h('th', { class: 'r' }, 'Costs'), h('th', {}, 'Then'))), body))),
          ],
          { id: `branch-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` },
        );
      }),
    );
  };
  const loadTree = () =>
    upgradeTree()
      .then((t) => {
        tree = t;
        drawTree();
        totals();
      })
      .catch((err) => branches.replaceChildren(problem(err)));
  const mine = h('button', { type: 'button', class: 'btn-small', 'data-upgrades-mine': '' }, 'Load your faction’s');
  mine.addEventListener('click', () =>
    void busy(mine, status, 'Asking Torn…', async () => {
      const [u, f] = await Promise.all([torn('/v2/faction/upgrades', {}, 5 * 60_000), torn('/faction/', { selections: 'basic' }, 5 * 60_000).catch(() => null)]);
      const read = readFactionUpgrades(u);
      if (!read) throw new Error('Torn’s answer about your faction’s upgrades came in a shape this page doesn’t know.');
      owned = read;
      factionRespect = n((f as Record<string, unknown> | null)?.respect);
      if (tree.length) drawTree();
      totals();
      status.textContent = `Your faction’s upgrades from Torn (${read.state === 'war' ? 'at war: war branches working' : 'at peace'}). Kept on this page only.`;
    }),
  );
  void loadTree();
  totals();
  return [
    box('Faction upgrades', [h('p', { class: 'muted small' }, 'Torn’s whole upgrade tree: pick the levels you want and see the respect they cost. Your faction’s own upgrades need a key with your faction’s API access.'), h('div', { class: 'toolbar' }, mine, status), tiles], { id: 'upgrades-calc' }),
    challenges,
    branches,
    howSure(
      sure('every branch, upgrade, level, ability, challenge and respect cost (Torn’s API: the faction tree); your faction’s upgrades and each branch’s “respect cost multiplier” (Torn’s API, with your faction’s API access).'),
      guess('a planned level costing the tree’s cost × its branch’s multiplier — our reading of Torn’s API docs; Torn gives no multiplier for a branch your faction hasn’t started, so that one is yours to set. Torn’s Upgrades page has the final say.'),
    ),
  ];
}

// ---------------------------------------------------------------- the page

const TABS: ReadonlyArray<{ id: string; label: string; body: () => HTMLElement[]; intro?: string }> = [
  { id: 'merits', label: 'Merits', body: meritsBody, intro: MERITS_INTRO },
  { id: 'respect', label: 'Respect', body: respectBody },
  { id: 'elo', label: 'Elo', body: eloBody },
  { id: 'gym', label: 'Gym gains', body: gymBody },
  { id: 'jump', label: 'Happy jump', body: jumpBody },
  { id: 'trip', label: 'Travel profit', body: tripBody },
  { id: 'fight', label: 'Fight', body: fightBody },
  { id: 'upgrades', label: 'Faction upgrades', body: upgradesBody },
];

export function renderCalc(view: HTMLElement): void {
  let tab = TABS.find((t) => t.id === new URLSearchParams(location.search).get('tab')) ?? TABS[0]!;
  const intro = h('p', { class: 'muted small' });
  const body = h('div', { 'data-calc': tab.id });
  const show = () => {
    body.dataset.calc = tab.id;
    intro.textContent = tab.intro ?? '';
    body.replaceChildren(...tab.body());
  };
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Calculators'), h('p', { class: 'muted' }, 'Torn’s sums with your own numbers, worked out in this browser. Where Torn hasn’t published how something works, the tab says so — those numbers are estimates.')),
    tabs(TABS.map((t) => ({ id: t.id, label: t.label })), tab.id, (id) => {
      tab = TABS.find((t) => t.id === id) ?? TABS[0]!;
      const u = new URL(location.href);
      if (tab === TABS[0]) u.searchParams.delete('tab');
      else u.searchParams.set('tab', tab.id);
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      show();
    }),
    intro,
    body,
  );
  show();
}
